import { getSport8RefundAudits } from '../../../api/sport8Api'
import YesOrNoTag from '../../../components/common/YesOrNoTag'
import { getDateTime } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum, UserDto } from '../../../share-types'
import { ILesson } from '../../booking-reviews-container/share-type'
import {
  IPaginatedSport8RefundAuditParams,
  IPaginatedSport8RefundAuditsResponse,
  ISport8RefundAudit,
} from '../share-type'
import { PlusOutlined, RetweetOutlined } from '@ant-design/icons'
import { Button, Col, Row, Space, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const Sport8RefundAudits = () => {
  const navigate = useNavigate()
  const [refundAudits, setRefundAudits] = useState<ISport8RefundAudit[]>()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)

  useEffect(() => {
    getPaginatedRefundAuditsAction({
      page: pageInfo.currentPage - 1,
      size: pageInfo.pageSize,
    })
  }, [])

  const getPaginatedRefundAuditsAction = (data: IPaginatedSport8RefundAuditParams) => {
    getSport8RefundAudits(data).then((res: IPaginatedSport8RefundAuditsResponse) => {
      if (res) {
        setRefundAudits(res.data)
        setPageInfo({
          ...res.pageInfo,
          currentPage: res.pageInfo.currentPage + 1,
        })
      }
    })
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    getPaginatedRefundAuditsAction({
      size: pageSize,
      page: page - 1,
    })
  }

  const columns: ColumnsType<ISport8RefundAudit> = [
    {
      title: translate('sport8RefundAudits.auditTradeNo'),
      dataIndex: ['studioIntegrationBookingInfo', 'auditTradeNo'],
      key: 'auditTradeNo',
    },
    {
      title: translate('sport8RefundAudits.bookingId'),
      dataIndex: ['booking', 'id'],
      key: 'bookingId',
      width: 50,
    },
    {
      title: translate('booking.externalProvider'),
      dataIndex: ['studioIntegrationBookingInfo', 'id'],
      key: 'externalProviderId',
    },
    {
      title: translate('sport8RefundAudits.price'),
      dataIndex: ['studioIntegrationBookingInfo', 'price'],
      key: 'price',
    },
    {
      title: translate('booking.user'),
      dataIndex: ['booking', 'user'],
      key: 'user',
      render: (user: UserDto) => {
        return user?.id
      },
    },
    {
      title: translate('booking.lesson'),
      dataIndex: ['booking', 'lesson'],
      key: 'lesson',
      render: (lesson: ILesson) => {
        return lesson?.id
      },
    },
    {
      title: translate('booking.noShow'),
      dataIndex: ['booking', 'noShow'],
      key: 'noShow',
      render: (noShow: boolean) => {
        return <YesOrNoTag value={noShow} />
      },
    },
    {
      title: translate('booking.completed'),
      dataIndex: ['booking', 'completed'],
      key: 'completed',
      render: (completed: boolean) => {
        return <YesOrNoTag value={completed} />
      },
    },
    {
      title: translate('booking.cancelled'),
      dataIndex: ['booking', 'cancelled'],
      key: 'cancelled',
      render: (cancelled: boolean) => {
        return <YesOrNoTag value={cancelled} />
      },
    },
    {
      title: translate('booking.lateCancelled'),
      dataIndex: ['booking', 'lateCancelled'],
      key: 'lateCancelled',
      render: (lateCancelled: boolean) => {
        return <YesOrNoTag value={lateCancelled} />
      },
    },
    {
      title: translate('updatedAt'),
      dataIndex: ['studioIntegrationBookingInfo', 'updatedAt'],
      key: 'updatedAt',
      render: (updatedAt: string) => {
        return <span>{getDateTime(updatedAt)}</span>
      },
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (record: ISport8RefundAudit) => (
        <AuthPermission permission={PermissionsEnum.SPORT8_REFUND_AUDIT_READ}>
          <Button type="link" onClick={() => onViewSport8RefundAudit(record)}>
            {translate('view')}
          </Button>
        </AuthPermission>
      ),
    },
  ]

  const onViewSport8RefundAudit = (record: ISport8RefundAudit) => {
    navigate(`/sport8-refund-audits/${record.booking.id}`)
  }

  return (
    <Space className="table-space" orientation="vertical" size={16}>
      <Row align="middle">
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
          <div>{translate('navBar.sport8RefundAuditsTitle')}</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className="right-col">
          <Space>
            <Button
              type="primary"
              icon={<RetweetOutlined />}
              onClick={() =>
                getPaginatedRefundAuditsAction({
                  page: defaultPage.currentPage - 1,
                  size: defaultPage.pageSize,
                })
              }
            >
              {translate('refresh')}
            </Button>
            <AuthPermission permission={PermissionsEnum.SPORT8_REFUND_AUDIT_WRITE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/sport8-refund-audits/new')}
              >
                {translate('sport8RefundAudits.createSport8RefundAudit')}
              </Button>
            </AuthPermission>
          </Space>
        </Col>
      </Row>
      <Table
        rowKey={record => record.booking.id}
        columns={columns}
        dataSource={refundAudits}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

export default Sport8RefundAudits
